import * as React from 'react';
import * as _ from 'lodash';
import { Redirect } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  ControlState,
  ControlStyleClass,
  ControlValidation,
  DisplayData,
  Notification,
  Logging,
  renderCheckboxWithHeading,
  renderEdit,
  renderSelect,
  Jurisdiction,
  API,
  AuditReport,
  NotificationData,
  ResponseError,
  AuditReportStatus,
  UserInfo,
  UserContext,
  renderDate,
  formatDate,
  DateFormatEnum,
  SearchAhead,
  CustomerSummary,
  TaxPayer,
  Customer,
  Footer,
  VertexLink,
} from '@vcd-ecm-ui/Common';
import { VtxButton, VtxIcon } from '@vertexinc/vtx-ui-react-component-library';
import AuditReportProps from './AuditReportProps.interface';
import '../common-reports.scss';
import './audit-reports.scss';
import useJurisdictions from '../Common/useJurisdictions';
import useCustomers from '../Common/useCustomers';
import useTaxpayers from '../Common/useTaxpayers';

const controlStyle: ControlStyleClass = null;

const controlStateEditable: ControlState = {
  editable: true,
  disabled: false,
  readOnly: false,
};

const controlStateDisabled: ControlState = {
  editable: false,
  disabled: true,
  readOnly: true,
};

const controlValidation: ControlValidation = {
  requiredFields: ['reportName', 'jurisdiction', 'auditBeginDate', 'auditEndDate'],
  customMessages: [],
};

const AuditReports = ({
  gatewayEndPoint,
}: AuditReportProps): JSX.Element => {
  const userContext: UserInfo = React.useContext(UserContext);

  const dispatch = useDispatch();

  const [notification, setNotification] = React.useState(null);
  const [redirect, setRedirect] = React.useState(null);

  const [searchCustomerCode, setSearchCustomerCode] = React.useState(null);
  const [customers, searchingCustomers, customersNotification] = useCustomers(gatewayEndPoint, searchCustomerCode);
  const [searchTaxpayerName, setSearchTaxpayerName] = React.useState(null);
  const [taxpayers, searchingTaxpayers, taxpayersNotification] = useTaxpayers(gatewayEndPoint, searchTaxpayerName);

  const [jurisdictions, jurisdictionNotification] = useJurisdictions(gatewayEndPoint);

  const [reportName, setReportName] = React.useState(null);
  const [jurisdiction, setJurisdiction] = React.useState(null);
  const [jurisdictionLevel, setJurisdictionLevel] = React.useState(false);
  const [customerUuid, setCustomerUuid] = React.useState(null); // Stores Customer Uuid
  const [auditBeginDate, setAuditBeginDate] = React.useState(null);
  const [auditEndDate, setAuditEndDate] = React.useState(null);
  const [taxpayerUuid, setTaxpayerUuid] = React.useState(null); // Stores Taxpayer Uuid

  const getCustomers = (): DisplayData[] => {
    if (customers) {
      // eslint-disable-next-line arrow-body-style
      return customers.map((item: CustomerSummary) => {
        return {
          data: item.customerUuid,
          display: item.customerCode,
        };
      });
    }

    return [];
  };

  const getTaxpayers = (): DisplayData[] => {
    if (taxpayers) {
      // eslint-disable-next-line arrow-body-style
      return taxpayers.map((item: TaxPayer) => {
        return {
          data: item.uuid,
          display: `${item.taxPayerName} / ${item.partitionName}`,
        };
      });
    }

    return [];
  };

  const searchCustomers = (searchValue: string) => {
    Logging.Debug('Searching Customers', searchValue);
    setSearchCustomerCode(searchValue);
  };

  const searchTaxpayers = (searchValue: string) => {
    Logging.Debug('Searching Taxpayers', searchValue);
    setSearchTaxpayerName(searchValue);
  };

  const getJurisdictions = (): DisplayData[] => {
    if (jurisdictions) {
      // eslint-disable-next-line arrow-body-style
      return jurisdictions.map((item: Jurisdiction) => {
        return {
          data: item.id.toString(),
          display: item.name,
        };
      });
    }

    return [];
  };

  const getJurisdiction = (): Jurisdiction => {
    if (jurisdiction) {
      const selectedJurisdictions: Jurisdiction[] = jurisdictions.filter((item: Jurisdiction) => item.id.toString() === jurisdiction);

      if (selectedJurisdictions.length === 1) {
        return selectedJurisdictions[0];
      }
    }

    return null;
  };

  // Make the API Call to Add the Audit Report
  const createAuditReport = (auditReport: AuditReport) => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      // Add the Audit Report
      await api.report.addAuditReportV41(auditReport);

      // Direct back to Audit Reports
      setRedirect('/reports/audits');
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Audit Report Save Error',
        description: `Unable to Create the Audit Report '${auditReport.reportName}'.`,
        error: error as ResponseError,
      };

      setNotification(notificationData);
    }
  };

  const onCreateAuditReport = (): void => {
    // Find the correct Customer by Uuid
    const customer: CustomerSummary = customers ? customers.filter((x) => x.customerUuid === customerUuid)[0] : null;

    // Find the Correct TaxPayer by Uuid
    const taxpayer: TaxPayer = taxpayerUuid && taxpayers ? taxpayers.filter((x) => x.uuid === taxpayerUuid)[0] : null;

    const auditReport: AuditReport = {
      reportName,
      status: AuditReportStatus.Queued,
      createdBy: userContext.givenName,
      jurisdiction: getJurisdiction(),
      includeLowerJurisdictions: jurisdictionLevel,
      auditBeginDate: formatDate(auditBeginDate, { dateFormat: DateFormatEnum.YMD, dateSeperator: '-' }),
      auditEndDate: formatDate(auditEndDate, { dateFormat: DateFormatEnum.YMD, dateSeperator: '-' }),
      customerCode: customer?.customerCode, // Save the Customer Code
      taxpayerDefinition: taxpayer
        ? {
          uuid: taxpayerUuid,
          partitionName: taxpayer.partitionName,
          departmentCode: taxpayer.departmentCode,
          divisionCode: taxpayer.divisionCode,
          companyCode: taxpayer.companyCode,
          taxPayerName: taxpayer.taxPayerName,
        }
        : null,
    };

    Logging.Info('Create Audit Report', auditReport);

    dispatch(createAuditReport(auditReport));
  };

  // Notification Error was Closed
  const onCloseNotification = (): void => {
    setNotification(null);
  };

  const validateValues = (): boolean => {
    if (!reportName) {
      return false;
    }

    if (!jurisdiction) {
      return false;
    }

    if (!auditBeginDate) {
      return false;
    }

    if (!auditEndDate) {
      return false;
    }

    if (auditBeginDate && auditEndDate) {
      if (auditEndDate < auditBeginDate) {
        return false;
      }
    }

    return true;
  };

  React.useEffect(() => {
    setNotification(jurisdictionNotification || customersNotification || taxpayersNotification);
  }, [jurisdictionNotification, customersNotification, taxpayersNotification]);

  React.useEffect(() => {
    // This Code will run once after the initial Render
    Logging.Debug('Audit Report: Mount');

    return (): void => {
      // This Code will run once the component unmounts.
      setRedirect(null);
    };
  }, []);

  React.useEffect(() => {
    // Reset the Jurisdiction Level if Jurisdiction is not selected
    setJurisdictionLevel(jurisdiction ? jurisdictionLevel : false);
  }, [jurisdiction]);

  const Spacer = () => <span style={{ marginRight: '10px' }} />;

  if (redirect) {
    return (
      <Redirect to={redirect} />
    );
  }

  // Clear the Custom Messages
  controlValidation.customMessages = [];

  if (auditBeginDate && auditEndDate) {
    if (auditEndDate < auditBeginDate) {
      controlValidation.customMessages = [...controlValidation.customMessages,
        { field: 'auditEndDate', message: 'Audit End Date must be on or after Audit Begin Date', type: 'Error' }];
    }
  }

  const canSave: boolean = validateValues();
  const jurisdictionSelected: boolean = jurisdiction !== null;
  const searchCustomerCodeAhead: SearchAhead = {
    searching: searchingCustomers,
    searchingText: 'Searching Customers ...',
    noResultsText: 'No Customers found',
    onSearch: searchCustomers,
    debounceMilliseconds: 750,
  };

  const searchTaxpayerNameAhead: SearchAhead = {
    searching: searchingTaxpayers,
    searchingText: 'Searching Taxpayers ...',
    noResultsText: 'No Taxpayers found',
    onSearch: searchTaxpayers,
    debounceMilliseconds: 750,
  };

  return (
    <>
      <div className="reportsContainer">
        <div className="reportsHeaderBack">
          <VertexLink href="#/reports/audits" display="Back to Audit Reports" />
        </div>
        <div className="reportsHeader">
          <div className="reportsHeaderLeft">
            Generate a New Audit Report
          </div>
        </div>
        <hr />
        <div className="reportsContentControls">
          {renderEdit(25, 'reportName', 'Report Name*', 'text', 'Report Name',
            reportName, setReportName, controlStateEditable, controlStyle, controlValidation)}
          {renderSelect(25, 'jurisdiction', 'Jurisdiction*', 'Select a Jurisdiction', false, true,
            getJurisdictions(), true, null, jurisdiction, setJurisdiction, controlStateEditable, controlStyle, controlValidation)}
          {renderCheckboxWithHeading(25, 'jurisdictionLevel', 'Jurisdiction Level', 'Include all lower jurisdictions', jurisdictionLevel,
            setJurisdictionLevel, jurisdictionSelected ? controlStateEditable : controlStateDisabled, controlStyle, controlValidation)}
          <div className="field25" />
          {renderSelect(25, 'customerCode', 'Customer Code', 'Search by Customer Code', false, false,
            getCustomers(), true, searchCustomerCodeAhead, customerUuid, setCustomerUuid, controlStateEditable, controlStyle, controlValidation)}
          {renderDate(25, 'auditBeginDate', 'Audit Begin Date*', 'Select Audit Begin Date', auditBeginDate,
            setAuditBeginDate, null, controlStateEditable, controlStyle, controlValidation)}
          {renderDate(25, 'auditEndDate', 'Audit End Date*', 'Select Audit End Date', auditEndDate,
            setAuditEndDate, null, controlStateEditable, controlStyle, controlValidation)}
          <div className="field25" />
          {renderSelect(25, 'taxpayerUuid', 'Taxpayer', 'Search by Taxpayer', false, false,
            getTaxpayers(), true, searchTaxpayerNameAhead, taxpayerUuid, setTaxpayerUuid, controlStateEditable, controlStyle, controlValidation)}
        </div>
        <Footer cancelLink="#/reports/audits" saveEnabled={canSave} onSave={onCreateAuditReport} saveText="Generate Audit Report" />
        <Notification
          data={notification}
          onNotificationClose={onCloseNotification}
          autoClose
        />
      </div>
    </>
  );
};

export default AuditReports;
